import axios from 'axios';
import { NO_COMPANY_SCOPE_REQUEST_CONFIG } from '../../hooks/dashboard';

export const getCheckoutSessionStatus = async (sessionId) => {
  let res = await axios.get('/billing/session-status', { params: { session_id: sessionId } });
  return res.data;
};

/**
 * ROAR-4943 — Credits & Usage reads (AC1, AC2, AC3).
 *
 * AC9: every one of these endpoints is company-scoped SERVER-SIDE by the P1.10 scope-resolver
 * middleware. The client sends no company identifier at all — not as an argument, not in the query
 * string, and not as a header: `NO_COMPANY_SCOPE_REQUEST_CONFIG` (exported from hooks/dashboard.js
 * by ROAR-4940) deletes the `company_id`/`selected_company_uuid` headers that src/index.js sets at
 * bootstrap. It must DELETE, not null them — axios 0.26 serializes a null header as "null".
 *
 * Importing that config pulls in hooks/dashboard.js's ESM-only `query-string` dependency, so any
 * test touching this module needs `jest.mock('query-string', () => ({ stringify: jest.fn() }))`.
 *
 * FIELD NAMES RATIFIED 2026-09-24 (ROAR-4945), and the request params were verified against the
 * shipped routes. These functions remain the single place each shape is mapped.
 */

/**
 * Credit lots and the available balance.
 *
 * `lots` is EVERY active credit lot regardless of `source` — `subscription` (self-serve), `manual`
 * (the enterprise per-period grant) and `beta` (promotional) — sorted `resets_at` ASC then `id`
 * ASC. `pct` is backend-computed, so the FE never divides; a 0-allotment lot arrives as `pct: 0`.
 *
 * There is deliberately no top-level `cycle` object: it restated the `subscription` lot's figures
 * under a server-resolved colour band, and that lot is already in `lots[]` labelled 'This Cycle'
 * (removed backend-side in ROAR-4899). `balance.reserved` is rendered beside `available`.
 *
 * @returns {Promise<{lots: Array<object>, balance: {available: number, reserved: number}}>}
 */
export const getCreditsSummary = async () => {
  const res = await axios.get('/billing/credits/summary', NO_COMPANY_SCOPE_REQUEST_CONFIG);
  return res.data;
};

/**
 * A page of the usage ledger.
 *
 * Unset facets are OMITTED rather than sent empty: `q=` or `user_id=` with a blank value is a
 * different request from no filter at all, and the backend would filter on the blank.
 *
 * Each item carries `entry_type` alongside `credits_used`: the amount is the MAGNITUDE
 * (`Math.abs(amount)`) and the direction lives in the type, so the two must be read together —
 * see `utils/constants/creditLedgerEntryTypes`.
 *
 * `user_id` is VALIDATED server-side: a malformed id and a valid id outside the scoped company
 * both answer **400**, identically and deliberately, so the endpoint cannot be used as a
 * cross-tenant user-existence oracle. `components/Settings/CreditsUsage/UsageLedger` treats that
 * 400 as a stale Member facet rather than a failed read.
 *
 * The response also carries `filters` — the option lists the filter panel's dropdowns are built
 * from. That used to be a second endpoint; the backend folded it into this one so the pane loads
 * in a single request. Those lists are deliberately NOT narrowed by the params sent here: they
 * cover every member and activity type the company has used, so choosing an option never prunes
 * the list it came from. `members[]` still fills gradually as the backend's actor stamp
 * accumulates rows, which is what makes the stale-facet 400 above reachable in normal use.
 *
 * @param {object} [params]
 * @param {number} [params.page] 1-based page number.
 * @param {number} [params.pageSize] Rows per page.
 * @param {string} [params.search] Free-text member/activity search.
 * @param {string} [params.from] Inclusive start date, `YYYY-MM-DD`.
 * @param {string} [params.to] Inclusive end date, `YYYY-MM-DD`.
 * @param {string} [params.userId] Member facet.
 * @param {string} [params.activityType] Activity-type facet.
 * @returns {Promise<{items: Array<object>, page: number, page_size: number, has_more?: boolean,
 *   total?: number, filters: {members: Array<{id: number, name: string|null}>,
 *   activity_types: Array<{key: string, label: string|null}>}}>}
 */
export const getUsageLedger = async ({
  page = 1,
  pageSize = 25,
  search,
  from,
  to,
  userId,
  activityType,
} = {}) => {
  const params = { page, page_size: pageSize };
  const trimmedSearch = (search || '').trim();
  if (trimmedSearch) params.q = trimmedSearch;
  if (from) params.from = from;
  if (to) params.to = to;
  if (userId) params.user_id = userId;
  if (activityType) params.activity_type = activityType;

  const res = await axios.get('/billing/credits/ledger', {
    ...NO_COMPANY_SCOPE_REQUEST_CONFIG,
    params,
  });
  return res.data;
};

/**
 * Plan and payment-method summary for the Payments & Invoices pane (ROAR-4944, AC4).
 *
 * `cancellation_requested_at` is the "requested but not yet effective" flag — non-null means a
 * cancellation is pending. **`plan.status` stays `active` for that whole window** (P1.12 owns the
 * `customer.subscription.deleted` webhook that finally changes it), so consumers must key the
 * cancelled state off `cancellation_requested_at` and never off `status` (§4 decision 16).
 *
 * `cancellation_requested_by` is deliberately NOT requested — the pane shows the date only, so the
 * backend need not resolve that user FK to a display name.
 *
 * `payment_method` is null when no card is on file; the pane renders "No card on file" and still
 * offers Manage Cards, since the Stripe portal is where a card gets added.
 *
 * @returns {Promise<{plan: object, payment_method: object|null}>}
 */
export const getBillingOverview = async () => {
  const res = await axios.get('/billing/overview', NO_COMPANY_SCOPE_REQUEST_CONFIG);
  return res.data;
};

/**
 * ROAR-5001 (P1.14) — the top-nav credits badge read (AC1-AC5, AC18).
 *
 * Unlike every other read in this file, this one deliberately KEEPS the bootstrap company-scope
 * headers (no `NO_COMPANY_SCOPE_REQUEST_CONFIG`). The badge renders for MSSP-level and Siemba users
 * too, and for them the company in scope is the one picked in `GlobalCompanySelection` — which the
 * backend's P1.10 scope-resolver reads from exactly those headers. Stripping them here would show
 * an MSSP user their own (nonexistent) company's balance instead of the selected one's (AC18).
 * Customer users are unaffected: the scope-resolver ignores the headers for a single-company user.
 *
 * `banner_state` is resolved server-side (`normal | warning | exhausted | trial_exhausted |
 * past_due`) and is the ONLY thing the badge keys its state off — the FE never re-derives it from
 * `pct` or `available`, so the badge cannot disagree with the backend's precedence rules.
 *
 * @returns {Promise<{available: number|null, cycle: object|null, company_status: string|null,
 *   subscription_status: string|null, trial_days_left: number|null,
 *   days_until_renewal: number|null, banner_state: string}>}
 */
export const getCreditsStatus = async () => {
  const res = await axios.get('/billing/credits/status');
  return res.data;
};

/**
 * ROAR-5001 (P1.14) — quote a feature's credit cost (AC6, AC10). A POST, but a pure read: the
 * backend never holds credits for an estimate, which is why it lives under queries/.
 *
 * Keeps the company-scope headers for the same reason as `getCreditsStatus`.
 *
 * Omitting `params` quotes the feature at its default settings, which is what
 * `default_estimated_credits` carries. An `included` or unpriceable feature answers with null
 * credit fields — callers must treat null as "no price", never as zero.
 *
 * @param {object} args
 * @param {string} args.featureKey A `billing.features` catalog key, e.g. `genpt_web`, `easm`.
 * @param {object} [args.params] The run's pricing inputs (`unit_count`, `auth`, `variant`).
 * @returns {Promise<{feature_key: string, cost_type: string, estimated_credits: number|null,
 *   default_estimated_credits: number|null, available: number|null, sufficient: boolean,
 *   rule_version: number|null}>}
 */
export const getCreditsEstimate = async ({ featureKey, params } = {}) => {
  const body = { feature_key: featureKey };
  if (params) body.params = params;
  const res = await axios.post('/billing/credits/estimate', body);
  return res.data;
};
